import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Search
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import { hasSuperAdminView } from '../../utils/rolePermissions';
import { filterRosterEligibleCrew } from '../../utils/crewRosterEligibility';
import {
  formatDateWithTimezone,
  utcToDisplayDateAndTime,
  displayDateTimeToUTC,
  getTimezoneLabel
} from '../../utils/timezoneUtils';

// Use same-origin API by default in browsers; allow override via REACT_APP_API_URL for local/dev tools
const API_URL = process.env.REACT_APP_API_URL || '/api';

const OpsAvailabilityAndLeave = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [tabValue, setTabValue] = useState(0); // 0: Availability, 1: Leave Requests
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const tzLabel = getTimezoneLabel(displayTimezone);

  // Availability state
  const [availability, setAvailability] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [selectedAvailability, setSelectedAvailability] = useState(null);
  const [availabilityFilters, setAvailabilityFilters] = useState({
    crewId: '',
    availabilityStatus: '',
    startDate: '',
    endDate: ''
  });
  const [availabilityFormData, setAvailabilityFormData] = useState({
    crewId: '',
    startDate: null,
    endDate: null,
    availabilityStatus: 'AVAILABLE',
    reason: '',
    remarks: ''
  });

  // Leave state
  const [requests, setRequests] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveFilters, setLeaveFilters] = useState({
    crewId: '',
    status: ''
  });
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('APPROVE');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionRemarks, setActionRemarks] = useState('');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyTitle, setHistoryTitle] = useState('');

  const canManage = user && [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(user.role);
  const canApprove = user && [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(user.role);

  const fetchCrewMembers = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      if (hasSuperAdminView(user?.role)) {
        try {
          const res = await axios.get(`${API_URL}/users`, { headers });
          const crew = filterRosterEligibleCrew(res.data || []);
          setCrewMembers(crew);
        } catch (usersError) {
          // If /users endpoint returns 403, fall back to crew-list endpoint
          if (usersError.response?.status === 403) {
            console.warn('Access denied to /users endpoint, falling back to crew-list');
            const res = await axios.get(`${API_URL}/ops/crew-duty-roster/crew-list`, { headers });
            setCrewMembers(res.data || []);
          } else {
            throw usersError; // Re-throw if it's a different error
          }
        }
      } else {
        const res = await axios.get(`${API_URL}/ops/crew-duty-roster/crew-list`, { headers });
        setCrewMembers(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching crew list:', error);
      // Show user-friendly error message
      if (error.response?.status === 403) {
        showToast('You do not have permission to view crew members', 'error');
      } else {
        showToast('Failed to fetch crew members. Please try again.', 'error');
      }
    }
  }, [user, showToast]);

  useEffect(() => {
    fetchCrewMembers();
  }, [fetchCrewMembers]);

  // ==================== Availability Functions ====================

  const fetchAvailability = useCallback(async () => {
    setAvailabilityLoading(true);
    try {
      const params = new URLSearchParams();
      if (availabilityFilters.crewId) params.append('crewId', availabilityFilters.crewId);
      if (availabilityFilters.startDate) params.append('startDate', availabilityFilters.startDate);
      if (availabilityFilters.endDate) params.append('endDate', availabilityFilters.endDate);
      if (availabilityFilters.availabilityStatus) params.append('availabilityStatus', availabilityFilters.availabilityStatus);

      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/availability?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAvailability(response.data || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
      showToast('Failed to fetch availability entries', 'error');
    } finally {
      setAvailabilityLoading(false);
    }
  }, [availabilityFilters, showToast]);

  const fetchRequests = useCallback(async () => {
    setLeaveLoading(true);
    try {
      const params = new URLSearchParams();
      if (leaveFilters.status) params.append('status', leaveFilters.status);
      if (leaveFilters.crewId) params.append('crewId', leaveFilters.crewId);

      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/leave-requests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      showToast('Failed to fetch leave requests', 'error');
    } finally {
      setLeaveLoading(false);
    }
  }, [leaveFilters, showToast]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchAvailability();
    } else {
      fetchRequests();
    }
  }, [tabValue, fetchAvailability, fetchRequests]);

  const handleCreateAvailability = () => {
    setSelectedAvailability(null);
    setAvailabilityFormData({
      crewId: '',
      startDate: dayjs(),
      endDate: dayjs().add(1, 'day'),
      availabilityStatus: 'AVAILABLE',
      reason: '',
      remarks: ''
    });
    setAvailabilityDialogOpen(true);
  };

  const handleEditAvailability = (entry) => {
    setSelectedAvailability(entry);
    const startD = entry.startDate ? utcToDisplayDateAndTime(entry.startDate, displayTimezone).date : '';
    const endD = entry.endDate ? utcToDisplayDateAndTime(entry.endDate, displayTimezone).date : '';
    setAvailabilityFormData({
      crewId: entry.crewId?._id || entry.crewId,
      startDate: startD ? dayjs(startD) : null,
      endDate: endD ? dayjs(endD) : null,
      availabilityStatus: entry.availabilityStatus,
      reason: entry.reason || '',
      remarks: entry.remarks || ''
    });
    setAvailabilityDialogOpen(true);
  };

  const handleSaveAvailability = async () => {
    try {
      if (!availabilityFormData.crewId) {
        showToast('Please select a crew member', 'error');
        return;
      }
      if (!availabilityFormData.startDate || !availabilityFormData.endDate) {
        showToast('Please select start and end dates', 'error');
        return;
      }
      const reasonTrimmed = (availabilityFormData.reason || '').trim();
      if (availabilityFormData.availabilityStatus !== 'AVAILABLE' && !reasonTrimmed) {
        showToast('Reason is required when status is not Available', 'error');
        return;
      }

      const startStr = availabilityFormData.startDate?.format?.('YYYY-MM-DD') || availabilityFormData.startDate;
      const endStr = availabilityFormData.endDate?.format?.('YYYY-MM-DD') || availabilityFormData.endDate;
      const startDateISO = startStr ? (displayDateTimeToUTC(startStr, '00:00', displayTimezone) ?? undefined) : undefined;
      const endDateISO = endStr ? (displayDateTimeToUTC(endStr, '23:59', displayTimezone) ?? undefined) : undefined;
      const payload = {
        crewId: availabilityFormData.crewId || undefined,
        startDate: startDateISO,
        endDate: endDateISO,
        availabilityStatus: availabilityFormData.availabilityStatus,
        reason: availabilityFormData.availabilityStatus !== 'AVAILABLE' ? reasonTrimmed : undefined,
        remarks: availabilityFormData.remarks
      };

      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

      if (selectedAvailability) {
        await axios.put(`${API_URL}/ops/crew-duty-roster/availability/${selectedAvailability._id}`, payload, { headers });
        showToast('Availability updated successfully', 'success');
      } else {
        await axios.post(`${API_URL}/ops/crew-duty-roster/availability`, payload, { headers });
        showToast('Availability created. Crew will be notified to acknowledge.', 'success');
      }

      setAvailabilityDialogOpen(false);
      fetchAvailability();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save availability', 'error');
    }
  };

  const handleDeleteAvailability = async (entry) => {
    if (!window.confirm('Are you sure you want to delete this availability entry?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/ops/crew-duty-roster/availability/${entry._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Availability entry deleted', 'success');
      fetchAvailability();
    } catch (error) {
      showToast('Failed to delete availability entry', 'error');
    }
  };

  const fetchAvailabilityHistory = async (entry) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.get(
        `${API_URL}/ops/crew-duty-roster/availability/${entry._id}/history`,
        { headers }
      );
      setHistoryTitle(`Availability History - ${getCrewName(entry)}`);
      setHistoryItems(res.data || []);
      setHistoryDialogOpen(true);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch availability history', 'error');
    }
  };

  const handleApproveAvailability = async (entry) => {
    try {
      await axios.put(`${API_URL}/ops/crew-duty-roster/availability/${entry._id}/approve`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Availability approved', 'success');
      fetchAvailability();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to approve availability', 'error');
    }
  };

  const handleRejectAvailability = async (entry) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await axios.put(`${API_URL}/ops/crew-duty-roster/availability/${entry._id}/reject`, {
        rejectionReason: reason
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Availability rejected', 'success');
      fetchAvailability();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to reject availability', 'error');
    }
  };

  const getAvailabilityStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'PENDING_APPROVAL': return 'warning';
      case 'ACKNOWLEDGED': return 'success';
      case 'PENDING_ACKNOWLEDGEMENT': return 'info';
      case 'AVAILABLE': return 'success';
      case 'UNAVAILABLE': return 'error';
      case 'ON_LEAVE': return 'info';
      case 'TRAINING': return 'warning';
      case 'MEDICAL': return 'error';
      default: return 'default';
    }
  };

  const getCrewDisplayLabel = (crew) =>
    crew ? `${crew.firstName || ''} ${crew.lastName || ''}`.trim() : '';

  const filterCrewBySearch = (options, { inputValue }) => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return options;
    return options.filter((crew) => {
      const name = getCrewDisplayLabel(crew).toLowerCase();
      const email = (crew.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  };

  const getCrewName = (entry) => {
    if (entry.crewId?.firstName) {
      return `${entry.crewId.firstName} ${entry.crewId.lastName || ''}`.trim();
    }
    const found = crewMembers.find(c => c._id === (entry.crewId?._id || entry.crewId));
    return found ? getCrewDisplayLabel(found) : 'Unknown Crew';
  };

  // ==================== Leave Functions ====================

  const getLeaveStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      case 'PENDING': return 'warning';
      case 'CANCELLED': return 'default';
      default: return 'default';
    }
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    return dayjs(end).diff(dayjs(start), 'day') + 1;
  };

  const openActionDialog = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setActionRemarks('');
    setActionDialogOpen(true);
  };

  const fetchLeaveHistory = async (request) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.get(
        `${API_URL}/ops/crew-duty-roster/leave-request/${request._id}/history`,
        { headers }
      );
      setHistoryTitle(`Leave Request History - ${getCrewNameForLeave(request)}`);
      setHistoryItems(res.data || []);
      setHistoryDialogOpen(true);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch leave request history', 'error');
    }
  };

  const handleAction = async () => {
    if (!selectedRequest) return;

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      if (actionType === 'APPROVE') {
        await axios.put(
          `${API_URL}/ops/crew-duty-roster/leave-request/${selectedRequest._id}/approve`,
          { remarks: actionRemarks || undefined },
          { headers }
        );
        showToast('Leave request approved', 'success');
      } else {
        await axios.put(
          `${API_URL}/ops/crew-duty-roster/leave-request/${selectedRequest._id}/reject`,
          { rejectionReason: actionRemarks || 'Rejected by Operations' },
          { headers }
        );
        showToast('Leave request rejected', 'success');
      }
      setActionDialogOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update leave request', 'error');
    }
  };

  const getCrewNameForLeave = (request) => {
    if (request.crewId?.firstName) {
      return `${request.crewId.firstName} ${request.crewId.lastName || ''}`.trim();
    }
    const found = crewMembers.find(c => c._id === (request.crewId?._id || request.crewId));
    return found ? `${found.firstName} ${found.lastName || ''}`.trim() : 'Unknown Crew';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Crew Availability & Leave Requests</Typography>
        </Box>

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="Crew Availability" />
          <Tab label="Leave Requests" />
        </Tabs>

        {/* Availability Tab */}
        {tabValue === 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateAvailability}
                >
                  Add Availability
                </Button>
              )}
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Crew Member</InputLabel>
                    <Select
                      value={availabilityFilters.crewId}
                      onChange={(e) => setAvailabilityFilters({ ...availabilityFilters, crewId: e.target.value })}
                      label="Crew Member"
                    >
                      <MenuItem value="">All</MenuItem>
                      {crewMembers.map((crew) => (
                        <MenuItem key={crew._id} value={crew._id}>
                          {crew.firstName} {crew.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={availabilityFilters.availabilityStatus}
                      onChange={(e) => setAvailabilityFilters({ ...availabilityFilters, availabilityStatus: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="AVAILABLE">Available</MenuItem>
                      <MenuItem value="UNAVAILABLE">Unavailable</MenuItem>
                      <MenuItem value="ON_LEAVE">On Leave</MenuItem>
                      <MenuItem value="TRAINING">Training</MenuItem>
                      <MenuItem value="MEDICAL">Medical</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label={tzLabel ? `Start Date (${tzLabel})` : 'Start Date'}
                    value={availabilityFilters.startDate}
                    onChange={(e) => setAvailabilityFilters({ ...availabilityFilters, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label={tzLabel ? `End Date (${tzLabel})` : 'End Date'}
                    value={availabilityFilters.endDate}
                    onChange={(e) => setAvailabilityFilters({ ...availabilityFilters, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Crew Member</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Availability</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Workflow Status</TableCell>
                    {canManage && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availabilityLoading ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 8 : 7} align="center">Loading...</TableCell>
                    </TableRow>
                  ) : availability.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canManage ? 8 : 7} align="center">No availability entries found</TableCell>
                    </TableRow>
                  ) : (
                    availability.map((entry) => (
                      <TableRow key={entry._id}>
                        <TableCell>{getCrewName(entry)}</TableCell>
                        <TableCell>{displayTimezone ? formatDateWithTimezone(entry.startDate, displayTimezone) : dayjs(entry.startDate).format('MMM DD, YYYY')}</TableCell>
                        <TableCell>{displayTimezone ? formatDateWithTimezone(entry.endDate, displayTimezone) : dayjs(entry.endDate).format('MMM DD, YYYY')}</TableCell>
                        <TableCell>
                          <Chip
                            label={entry.availabilityStatus.replace(/_/g, ' ')}
                            color={getAvailabilityStatusColor(entry.availabilityStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{entry.reason || '-'}</TableCell>
                        <TableCell>
                          {entry.status && (
                            <>
                              <Chip
                                label={entry.status.replace(/_/g, ' ')}
                                color={getAvailabilityStatusColor(entry.status)}
                                size="small"
                                sx={{ mb: 1 }}
                              />
                              {entry.status === 'REJECTED' && entry.rejectionReason && (
                                <Alert severity="error" sx={{ py: 0, mt: 1 }}>
                                  {entry.rejectionReason}
                                </Alert>
                              )}
                            </>
                          )}
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            {entry.status === 'PENDING_APPROVAL' && (
                              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleApproveAvailability(entry)}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleRejectAvailability(entry)}
                                >
                                  Reject
                                </Button>
                              </Box>
                            )}
                            <Tooltip title="View History">
                              <IconButton size="small" onClick={() => fetchAvailabilityHistory(entry)}>
                                <Search fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditAvailability(entry)}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDeleteAvailability(entry)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Availability Create/Edit Dialog */}
            <Dialog open={availabilityDialogOpen} onClose={() => setAvailabilityDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>{selectedAvailability ? 'Edit Availability' : 'Add Availability'}</DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={crewMembers}
                      getOptionLabel={getCrewDisplayLabel}
                      filterOptions={filterCrewBySearch}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      value={
                        crewMembers.find((c) => c._id === availabilityFormData.crewId) || null
                      }
                      onChange={(_, crew) =>
                        setAvailabilityFormData({
                          ...availabilityFormData,
                          crewId: crew?._id || ''
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Crew Member"
                          required
                          placeholder="Type to search by name or email"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label={tzLabel ? `Start Date (${tzLabel})` : 'Start Date'}
                      value={availabilityFormData.startDate}
                      onChange={(newValue) => setAvailabilityFormData({ ...availabilityFormData, startDate: newValue })}
                      slotProps={{ textField: { fullWidth: true, required: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label={tzLabel ? `End Date (${tzLabel})` : 'End Date'}
                      value={availabilityFormData.endDate}
                      onChange={(newValue) => setAvailabilityFormData({ ...availabilityFormData, endDate: newValue })}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Availability Status</InputLabel>
                      <Select
                        value={availabilityFormData.availabilityStatus}
                        onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, availabilityStatus: e.target.value })}
                        label="Availability Status"
                      >
                        <MenuItem value="AVAILABLE">Available</MenuItem>
                        <MenuItem value="UNAVAILABLE">Unavailable</MenuItem>
                        <MenuItem value="ON_LEAVE">On Leave</MenuItem>
                        <MenuItem value="TRAINING">Training</MenuItem>
                        <MenuItem value="MEDICAL">Medical</MenuItem>
                        <MenuItem value="OTHER">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {availabilityFormData.availabilityStatus !== 'AVAILABLE' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Reason"
                        value={availabilityFormData.reason}
                        onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, reason: e.target.value })}
                        required
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Remarks"
                      value={availabilityFormData.remarks}
                      onChange={(e) => setAvailabilityFormData({ ...availabilityFormData, remarks: e.target.value })}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAvailabilityDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveAvailability} variant="contained">
                  {selectedAvailability ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {/* Leave Requests Tab */}
        {tabValue === 1 && (
          <>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={leaveFilters.status}
                      onChange={(e) => setLeaveFilters({ ...leaveFilters, status: e.target.value })}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="APPROVED">Approved</MenuItem>
                      <MenuItem value="REJECTED">Rejected</MenuItem>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Crew Member</InputLabel>
                    <Select
                      value={leaveFilters.crewId}
                      onChange={(e) => setLeaveFilters({ ...leaveFilters, crewId: e.target.value })}
                      label="Crew Member"
                    >
                      <MenuItem value="">All</MenuItem>
                      {crewMembers.map((crew) => (
                        <MenuItem key={crew._id} value={crew._id}>
                          {crew.firstName} {crew.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Leave Requests Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Crew Member</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Days</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Response</TableCell>
                    {canApprove && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaveLoading ? (
                    <TableRow>
                      <TableCell colSpan={canApprove ? 9 : 8} align="center">Loading...</TableCell>
                    </TableRow>
                  ) : requests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canApprove ? 9 : 8} align="center">No leave requests found</TableCell>
                    </TableRow>
                  ) : (
                    requests.map((request) => (
                      <TableRow key={request._id}>
                        <TableCell>{getCrewNameForLeave(request)}</TableCell>
                        <TableCell>{displayTimezone ? formatDateWithTimezone(request.leaveStartDate, displayTimezone) : dayjs(request.leaveStartDate).format('MMM DD, YYYY')}</TableCell>
                        <TableCell>{displayTimezone ? formatDateWithTimezone(request.leaveEndDate, displayTimezone) : dayjs(request.leaveEndDate).format('MMM DD, YYYY')}</TableCell>
                        <TableCell>{calculateDays(request.leaveStartDate, request.leaveEndDate)}</TableCell>
                        <TableCell>{request.leaveType}</TableCell>
                        <TableCell>
                          <Chip
                            icon={request.status === 'APPROVED' ? <CheckCircle /> : request.status === 'REJECTED' ? <Cancel /> : null}
                            label={request.status}
                            color={getLeaveStatusColor(request.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{request.reason}</TableCell>
                        <TableCell>
                          {request.status === 'REJECTED' && request.rejectionReason && (
                            <Alert severity="error" sx={{ py: 0 }}>
                              {request.rejectionReason}
                            </Alert>
                          )}
                          {request.status === 'APPROVED' && request.approvedBy && (
                            <Typography variant="body2" color="success.main">
                              Approved by {request.approvedBy.firstName} {request.approvedBy.lastName}
                            </Typography>
                          )}
                          {request.status === 'PENDING' && (
                            <Typography variant="body2" color="text.secondary">
                              Pending approval
                            </Typography>
                          )}
                        </TableCell>
                        {canApprove && (
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {request.status === 'PENDING' && (
                                <>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={() => openActionDialog(request, 'APPROVE')}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => openActionDialog(request, 'REJECT')}
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => fetchLeaveHistory(request)}
                              >
                                History
                              </Button>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

      {/* Approve / Reject Dialog */}
            <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>{actionType === 'APPROVE' ? 'Approve Leave Request' : 'Reject Leave Request'}</DialogTitle>
              <DialogContent>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={actionType === 'APPROVE' ? 'Approval Remarks (optional)' : 'Rejection Reason'}
                  value={actionRemarks}
                  onChange={(e) => setActionRemarks(e.target.value)}
                  sx={{ mt: 2 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleAction}
                  variant="contained"
                  color={actionType === 'APPROVE' ? 'success' : 'error'}
                >
                  {actionType === 'APPROVE' ? 'Approve' : 'Reject'}
                </Button>
        </DialogActions>
      </Dialog>

      {/* Audit History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{historyTitle || 'Change History'}</DialogTitle>
        <DialogContent dividers>
          {historyItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No history records found.
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              {historyItems.map((h) => (
                <Box key={h.id} sx={{ mb: 2, p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {h.action} by {h.actor?.name || 'System'} ({h.actor?.role || 'N/A'}) on{' '}
                    {dayjs(h.createdAt).format('DD MMM YYYY HH:mm')}
                  </Typography>
                  {h.meta && h.meta.comment && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Comment: {h.meta.comment}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default OpsAvailabilityAndLeave;

